import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import Ninja from '@ninja';
import { Spin, Typography, Divider } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import useQueryParams from 'hooks/useQueryParams';
import { DateFormatter } from 'components/charts/formatters';
import ninjaApi from 'store/redux/apis';
import getFormattedNumber from 'store/redux/utils/getFormattedNumber';
import { FormatKeyToLabel } from 'components/tables/NinjaTable/formatters';
import { useJourneyReport } from 'modules/ReportModule/context/useJourneyReport';
import getShortFormattedNumber from 'store/redux/utils/getShortFormattedNumber';

const EventsOverviewChart = () => {
  const { searchParams } = useQueryParams();
  const [eventsOverviewQuery, { data = [], isLoading, isFetching }] =
    ninjaApi.useLazyEventsOverviewQuery();
  const { user_id, website_id } = searchParams;
  const { filter } = useJourneyReport();

  useEffect(() => {
    if (user_id) {
      eventsOverviewQuery({
        filters: {
          user_id: user_id,
          ...filter,
        },
      }).unwrap();
    }
  }, [website_id, user_id, filter]);

  const [activeIndex, setActiveIndex] = useState(null);

  const getBarStyle = (index) => {
    if (activeIndex === null) return { opacity: 1, transition: 'all 0.3s ease-in-out' };
    return {
      opacity: index === activeIndex ? 1 : 0.2,
      transition: 'all 0.3s ease-in-out',
    };
  };

  const handleMouseMove = (e) => {
    if (e?.activeTooltipIndex !== undefined) {
      setActiveIndex(e.activeTooltipIndex);
    }
  };

  if (isLoading || isFetching)
    return (
      <div className='flex items-center justify-center !h-[350px]'>
        <Spin
          indicator={<LoadingOutlined spin />}
          size='large'
          className='flex justify-center mt-5'
        />
      </div>
    );

  if (!data?.data?.length) {
    return (
      <Typography className='!h-[350px] flex flex-col items-center justify-center w-full  text-xs font-normal text-color-light'>
        No data found for this date range.
      </Typography>
    );
  }

  return (
    <ResponsiveContainer width='100%' height={350}>
      <BarChart
        onMouseLeave={() => setActiveIndex(null)}
        onMouseMove={handleMouseMove}
        data={data?.data}
        margin={{ left: -20, right: 0, top: 20, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='#d9d9d9' />
        <XAxis
          axisLine={false}
          dataKey='name'
          dy={5}
          tick={{ fill: Ninja.colors.darkText, fontSize: 11, fontWeight: 500 }}
          tickFormatter={(value) =>
            value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
          }
          angle={-30}
          textAnchor='end'
          height={90}
        />

        <YAxis
          tickFormatter={(value) => getShortFormattedNumber(value)}
          tick={{ fill: Ninja.colors.darkText, fontSize: 11, fontWeight: 500 }}
          axisLine={false}
        />
        <Tooltip
          active
          cursor={{
            fill: Ninja.colors.info,
            fillOpacity: 0.2,
            transition: 'all 0.2s ease-in-out',
          }}
          content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const item = payload[0].payload;
              return (
                <div className='bg-white p-2 rounded-md shadow text-sm'>
                  <Typography className='text-center font-semibold text-lg  mb-1'>
                    <FormatKeyToLabel value={item.name} />
                  </Typography>

                  <div className='flex items-center justify-between gap-4'>
                    <span className='text-sm font-normal'>Volume</span>
                    <span bordered={false} className='font-medium text-base'>
                      {getFormattedNumber(item.volume)}
                    </span>
                  </div>

                  <div className='flex items-center justify-between gap-4'>
                    <span className='text-sm font-normal'>Percentage</span>
                    <span bordered={false} className='font-medium text-base'>
                      {getFormattedNumber(item.percentage_of_volume)} %
                    </span>
                  </div>
                  <Divider />

                  <div className='grid grid-cols-2 gap-6'>
                    <div>
                      <span className='block text-xs text-gray'>First seen</span>
                      <DateFormatter
                        date={item.first_seen}
                        format='MMM DD, YYYY'
                        className='font-medium '
                      />
                    </div>

                    <div>
                      <span className='block text-xs text-gray'>Last seen</span>
                      <DateFormatter
                        date={item.last_seen}
                        format='MMM DD, YYYY'
                        className='font-medium '
                      />
                    </div>
                  </div>
                </div>
              );
            }
            return null;
          }}
        />

        <Bar
          dataKey='volume'
          maxBarSize={10}
          fill='#4173CD'
          shape={({ x, y, width, height, index }) => {
            const color = data?.data[index].color;
            const radius = 3;
            const gap = 2;
            const isNegative = height < 0;

            return (
              <rect
                x={x}
                y={isNegative ? y + height + gap / 2 : y + gap / 2}
                width={width}
                height={Math.max(0, Math.abs(height))}
                rx={radius}
                ry={radius}
                fill='#4173CD'
                style={getBarStyle(index)}
              />
            );
          }}
        />
        <ReferenceLine y={0} stroke='#f0f0f0' />
      </BarChart>
    </ResponsiveContainer>
  );
};

export default EventsOverviewChart;
